import { DialogBody, DialogTitle } from '@/components/Dialog'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'
import { TextInput } from '@/UIComponents2/TextInput'
import { SubRoomFragment, useUserContext } from '@/UserContext'
import { isGuestUsername, numberToColumnLabel } from '@/utils'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useRoomContextRef from '@/services/useRoomContextRef'

export default function EditStudioSettingsDialog({
    subroom,
    isLoading,
    onSubmit,
    onClose,
}: {
    subroom: SubRoomFragment | null
    isLoading: boolean
    onSubmit: (
        mode: 'edit' | 'create',
        data: { roomUrn: string | null; subroomName: string; collaborators: string[] }
    ) => void
    onClose: () => void
}) {
    const { t } = useTranslation()

    const [userState] = useUserContext()
    const { roomStateRef } = useRoomContextRef()

    const [subroomName, setSubroomName] = useState('')
    const [collaborators, setCollaborators] = useState<string[]>([])

    const mode = subroom ? 'edit' : 'create'

    useEffect(() => {
        if (mode === 'create') {
            const nextN = (roomStateRef.current.subrooms || []).length + 1
            const subroomName = `Studio ${numberToColumnLabel(nextN)}`
            setSubroomName(subroomName)
        } else if (subroom) {
            setSubroomName(subroom.name)
            // TODO
            setCollaborators([])
        }
    }, [roomStateRef, mode])

    const suggestedCollaborators = useMemo(() => {
        let users = [...(userState.profile?.knownUsers ?? [])]
        users = users.filter((u) => !isGuestUsername(u.username))
        users.reverse()
        return users
    }, [userState.profile])

    const handleRemoveCollaborator = (username: string) => {
        setCollaborators(collaborators.filter((u) => u !== username))
    }

    const handleSubmit = () => {
        // usernames to userUrns
        const userUrns = collaborators
            .map((username) => {
                return suggestedCollaborators.find((obj) => obj.username === username)?.userUrn || null
            })
            .filter((a) => a != null)

        onSubmit(mode, { roomUrn: subroom?._id || null, subroomName, collaborators: userUrns })
        onClose()
    }

    return (
        <CrossPlatformDialog
            browserZIndex={21}
            forceBrowserModal
            classNameBrowser="w-full md:w-[600px] max-h-[600px] max-h-[80vh]"
            onDefaultClose={onClose}
        >
            <DialogBody>
                <DialogTitle>{mode === 'create' ? 'Create Studio' : 'Edit Studio'}</DialogTitle>

                <div className="flex flex-col gap-5 mb-3">
                    <div>Create and set up a studio for group session</div>

                    <TextInput label="Studio Name" value={subroomName} onChange={setSubroomName} />

                    {/* TODO: work in progress */}
                    {/*
                    <CollaboratorSelector
                        onSubmit={(usernames: string[]) => {
                            setCollaborators(usernames)
                        }}
                        label={t('Assign users to studio by username (optional)')}
                        suggestions={suggestedCollaborators.map((u) => u.username)}
                    />

                    <div className="flex flex-col gap-2 pt-3">
                        {collaborators.length === 0 ? (
                            <div>No users assigned.</div>
                        ) : (
                            <div>Users assigned to this studio</div>
                        )}
                        <div className="flex flex-col gap-1">
                            {collaborators.map((username: string) => (
                                <div key={username} className="flex gap-2 items-center">
                                    <UserIcon />
                                    <div className="flex-1">{username}</div>
                                    <Button
                                        variant="basic"
                                        onClick={() => handleRemoveCollaborator(username)}
                                        className="w-8 h-8"
                                    >
                                        <TrashIcon />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                    */}
                </div>
            </DialogBody>

            <DialogActionButtonsSection
                rightButtonText={mode === 'create' ? t('Create') : t('Update')}
                isRightButtonDisabled={!!isLoading}
                onRightButtonClicked={handleSubmit}
            />
        </CrossPlatformDialog>
    )
}
